import { memo, useCallback } from 'react';
import {
  Label,
  Textarea,
  Separator,
  SelectOrCreate,
  SelectItem,
} from '@flote/ui';
import { User, MapPin, Truck } from 'lucide-react';
import { useGooglePlaces, type ParsedAddress } from '@flote/app-core';
import { RecipientInput, AddressInput, TimeWindowInput, CompletionRequirements } from '../inputs';
import { OrderSelector } from './OrderSelector';
import { TeamDriverAssignment } from './TeamDriverAssignment';
import type {
  DropoffState,
  AddressState,
  RecipientState,
  TimeWindowState,
  CompletionRequirementsState,
} from '../hooks';
import type { Team, Recipient, Destination, B2BWaveOrderSummary, Driver } from '@flote/contracts/http';

interface DropoffSectionProps {
  dropoff: DropoffState;
  // Order
  orders: B2BWaveOrderSummary[];
  selectedOrder: B2BWaveOrderSummary | undefined;
  onOrderChange: (orderId: string) => void;
  // Team/Driver
  teams: Team[];
  drivers: Driver[];
  isLoadingDrivers: boolean;
  onTeamChange: (teamId: string) => void;
  onDriverChange: (driverId: string) => void;
  onAutoAssignChange: (autoAssign: boolean) => void;
  // Recipient
  recipients: Recipient[];
  selectedRecipient: Recipient | undefined;
  onRecipientIdChange: (id: string | undefined) => void;
  onRecipientModeChange: (mode: 'select' | 'create') => void;
  onRecipientChange: (field: keyof RecipientState, value: string) => void;
  // Destination
  destinations: Destination[];
  selectedDestination: Destination | undefined;
  onDestinationIdChange: (id: string | undefined) => void;
  onDestinationModeChange: (mode: 'select' | 'create') => void;
  onAddressChange: (field: keyof AddressState, value: string) => void;
  onAddressSelect: (address: AddressState) => void;
  // Notes, Time, Requirements
  onNotesChange: (notes: string) => void;
  onTimeWindowChange: (field: keyof TimeWindowState, value: string) => void;
  onCompletionRequirementsChange: (field: keyof CompletionRequirementsState, value: boolean) => void;
  // Misc
  errors: Record<string, string>;
  disabled?: boolean;
  isLinkedTask?: boolean;
  noTeamValue: string;
  noOrderValue: string;
  noDriverValue: string;
  orderRequired?: boolean;
  showDriverSelect?: boolean;
}

export const DropoffSection = memo<DropoffSectionProps>(({
  dropoff,
  orders,
  selectedOrder,
  onOrderChange,
  teams,
  drivers,
  isLoadingDrivers,
  onTeamChange,
  onDriverChange,
  onAutoAssignChange,
  recipients,
  selectedRecipient,
  onRecipientIdChange,
  onRecipientModeChange,
  onRecipientChange,
  destinations,
  selectedDestination,
  onDestinationIdChange,
  onDestinationModeChange,
  onAddressChange,
  onAddressSelect,
  onNotesChange,
  onTimeWindowChange,
  onCompletionRequirementsChange,
  errors,
  disabled = false,
  isLinkedTask = false,
  noTeamValue,
  noOrderValue,
  noDriverValue,
  orderRequired = false,
  showDriverSelect = true,
}) => {
  // Handle Google Places autocomplete selection
  const handlePlaceSelect = useCallback((parsedAddress: ParsedAddress) => {
    onAddressSelect({
      search: parsedAddress.formattedAddress,
      streetNumber: parsedAddress.number,
      streetAddress: parsedAddress.street,
      apartment: parsedAddress.apartment,
      city: parsedAddress.city,
      state: parsedAddress.state,
      postalCode: parsedAddress.postalCode,
    });
  }, [onAddressSelect]);

  const { inputRef: addressSearchRef } = useGooglePlaces(
    import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    { onPlaceSelect: handlePlaceSelect }
  );

  const isNewRecipient = dropoff.recipientMode === 'create';
  const isNewDestination = dropoff.destinationMode === 'create';

  return (
    <>
      {/* Dropoff header for linked mode */}
      {isLinkedTask && (
        <div className="flex items-center gap-2 text-sm font-medium">
          <Truck className="h-4 w-4 text-muted-foreground" />
          Dropoff Details
        </div>
      )}

      {/* Order Selection */}
      <OrderSelector
        orders={orders}
        selectedOrderId={dropoff.selectedOrderId}
        onOrderChange={onOrderChange}
        selectedOrder={selectedOrder}
        disabled={disabled}
        error={errors['dropoff.orderId']}
        noOrderValue={noOrderValue}
        orderRequired={orderRequired}
      />

      <Separator />

      {/* Team Assignment */}
      <TeamDriverAssignment
        teams={teams}
        selectedTeamId={dropoff.selectedTeamId}
        onTeamChange={onTeamChange}
        autoAssign={dropoff.autoAssign}
        onAutoAssignChange={onAutoAssignChange}
        drivers={drivers}
        selectedDriverId={dropoff.selectedDriverId}
        onDriverChange={onDriverChange}
        isLoadingDrivers={isLoadingDrivers}
        disabled={disabled}
        noTeamValue={noTeamValue}
        noDriverValue={noDriverValue}
        showDriverSelect={showDriverSelect}
      />

      <Separator />

      {/* Recipient Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <User className="h-4 w-4 text-muted-foreground" />
          Recipient
        </div>

        <SelectOrCreate.Root
          selectedValue={dropoff.selectedRecipientId}
          onSelectedValueChange={onRecipientIdChange}
          onModeChange={onRecipientModeChange}
          defaultMode={recipients.length > 0 ? 'select' : 'create'}
          disabled={disabled}
        >
          <div className="space-y-2">
            <Label>Select Existing Recipient</Label>
            <SelectOrCreate.Select placeholder="Select a recipient">
              {recipients.map((recipient) => (
                <SelectItem key={recipient.id} value={recipient.id}>
                  {recipient.name}
                  {recipient.phone && (
                    <span className="ml-1 text-muted-foreground">({recipient.phone})</span>
                  )}
                </SelectItem>
              ))}
            </SelectOrCreate.Select>
          </div>

          <SelectOrCreate.Preview>
            {selectedRecipient && (
              <>
                <p className="text-sm font-medium">{selectedRecipient.name}</p>
                {selectedRecipient.phone && (
                  <p className="text-xs text-muted-foreground">{selectedRecipient.phone}</p>
                )}
              </>
            )}
          </SelectOrCreate.Preview>

          <SelectOrCreate.Button>
            {isNewRecipient ? 'Cancel' : 'Create New Recipient'}
          </SelectOrCreate.Button>

          <SelectOrCreate.Form>
            <RecipientInput
              recipient={dropoff.recipient}
              onRecipientChange={onRecipientChange}
              errors={errors}
              errorPrefix="dropoff.recipient."
              disabled={disabled}
            />
          </SelectOrCreate.Form>
        </SelectOrCreate.Root>
      </div>

      <Separator />

      {/* Destination Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          Delivery Destination
        </div>

        <SelectOrCreate.Root
          selectedValue={dropoff.selectedDestinationId}
          onSelectedValueChange={onDestinationIdChange}
          onModeChange={onDestinationModeChange}
          defaultMode={destinations.length > 0 ? 'select' : 'create'}
          disabled={disabled}
        >
          <div className="space-y-2">
            <Label>Select Existing Destination</Label>
            <SelectOrCreate.Select placeholder="Select a destination">
              {destinations.map((destination) => (
                <SelectItem key={destination.id} value={destination.id}>
                  {destination.address?.street && destination.address?.number
                    ? `${destination.address.number} ${destination.address.street}`
                    : 'Unknown address'}
                  {destination.address?.city && (
                    <span className="ml-1 text-muted-foreground">
                      ({destination.address.city})
                    </span>
                  )}
                </SelectItem>
              ))}
            </SelectOrCreate.Select>
          </div>

          <SelectOrCreate.Preview>
            {selectedDestination && (
              <>
                <p className="text-sm font-medium">
                  {selectedDestination.address?.number} {selectedDestination.address?.street}
                  {selectedDestination.address?.apartment && `, ${selectedDestination.address.apartment}`}
                </p>
                <p className="text-xs text-muted-foreground">
                  {selectedDestination.address?.city}, {selectedDestination.address?.state} {selectedDestination.address?.postalCode}
                </p>
                {selectedDestination.notes && (
                  <p className="text-xs text-muted-foreground mt-1 italic">{selectedDestination.notes}</p>
                )}
              </>
            )}
          </SelectOrCreate.Preview>

          <SelectOrCreate.Button>
            {isNewDestination ? 'Cancel' : 'Enter New Address'}
          </SelectOrCreate.Button>

          <SelectOrCreate.Form>
            <AddressInput
              address={dropoff.address}
              onAddressChange={onAddressChange}
              inputRef={addressSearchRef}
              errors={errors}
              errorPrefix="dropoff."
              disabled={disabled}
            />
          </SelectOrCreate.Form>
        </SelectOrCreate.Root>
      </div>

      <Separator />

      {/* Notes Section */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            placeholder="Delivery instructions, gate codes, etc."
            value={dropoff.notes}
            onChange={(e) => onNotesChange(e.target.value)}
            disabled={disabled}
            rows={2}
          />
        </div>
      </div>

      <Separator />

      {/* Time Window */}
      <TimeWindowInput
        timeWindow={dropoff.timeWindow}
        onTimeWindowChange={onTimeWindowChange}
        disabled={disabled}
      />

      <Separator />

      {/* Completion Requirements */}
      <CompletionRequirements
        requirements={dropoff.completionRequirements}
        onRequirementChange={onCompletionRequirementsChange}
        disabled={disabled}
        signatureLabel="Customer signature"
        photoLabel="Photo of delivery"
        notesLabel="Add notes"
      />
    </>
  );
});

DropoffSection.displayName = 'DropoffSection';
